/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowPathIcon, CheckIcon, TrashIcon } from '@heroicons/react/24/outline'
import { ColumnFiltersState, SortingState, VisibilityState, ColumnOrderState, Table } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import UiButton, { UiButtonSize, UiButtonStyle } from 'components/Ui/UiButton'
import UiTableColumnVisibilityPanel from 'components/Ui/UiTableColumnVisibilityPanel/UiTableColumnVisibilityPanel'
import { useAppDispatch } from 'helpers/store'
import { useTypedSelector } from 'helpers/reducers'
import TableViewsAction from 'store/tableViews/TableViewsAction'
import { setActiveViewId as setActiveViewIdInRedux } from 'store/tableViews/TableViewsReducer'
import { SavedTableView } from 'types/entities/SavedTableView'
import { TABLE_VIEW_PAGE_NAME, TableViewId } from 'types/enums/specific/TableViewId'
import { TableInitBase } from 'utils/hooks/useTable'
import {
    clearActiveTableView,
    hydrateActiveTableViewFromSession,
    persistActiveTableViewId,
} from 'utils/table/activeTableView'
import { findSavedViewByName, normalizeSavedViewName } from 'utils/table/savedTableViewName'
import { toNumericViewId } from 'utils/table/tableViewId'
import { toast } from 'utils/toastUtils'
import {
    getLeadSystemPresetViews,
    isSystemSavedTableView,
} from 'utils/leads/leadFollowUpPresets'

export interface TableSaveViewApi {
    columnFilters: ColumnFiltersState
    globalFilter: string
    sorting: SortingState
    columnVisibility: VisibilityState
    columnOrder: ColumnOrderState
    pageSize: number
    table?: Table<any>
    setColumnFilters: (filters: ColumnFiltersState) => void
    setGlobalFilter: (value: string) => void
    setSorting: (sorting: SortingState) => void
    setColumnVisibility?: (visibility: VisibilityState) => void
    setColumnOrder?: (order: ColumnOrderState) => void
    setPagination: (pagination: { pageIndex: number; pageSize: number }) => void
}

export { getActiveTableViewStorageKey } from 'utils/table/activeTableView'

export const toTableSaveViewApi = (tableInit: TableInitBase): TableSaveViewApi => ({
    columnFilters: tableInit.columnFilters,
    globalFilter: tableInit.globalFilter,
    sorting: tableInit.sorting,
    columnVisibility: tableInit.columnVisibility,
    columnOrder: tableInit.columnOrder,
    pageSize: tableInit.pageSize,
    table: tableInit.table,
    setColumnFilters: tableInit.setColumnFilters,
    setGlobalFilter: tableInit.setGlobalFilter,
    setSorting: tableInit.setSorting,
    setColumnVisibility: tableInit.setColumnVisibility,
    setColumnOrder: tableInit.setColumnOrder,
    setPagination: tableInit.setPagination,
})

interface UiTableViewDropdownPanelProps {
    tableInit: TableSaveViewApi
    tableId: TableViewId
    open: boolean
    onClose: () => void
}

const UiTableViewDropdownPanel: React.FC<UiTableViewDropdownPanelProps> = ({
    tableInit,
    tableId,
    open,
    onClose,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [viewName, setViewName] = useState('')
    const [saving, setSaving] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const seller_id = toNumericViewId(useTypedSelector((state) => state.sessionState.session?.uid))
    const activeViewId = useTypedSelector((state) => state.tableViewsState.activeViewIds?.[tableId] ?? null)

    const allViews = useTypedSelector((state) => state.tableViewsState.views)
    const hasLoaded = useTypedSelector((state) => state.tableViewsState.hasLoaded)
    const views = useMemo(() => {
        const sellerViews = allViews
            .filter((v: SavedTableView) => v.table_id == tableId)
            .filter(
                (v: SavedTableView) =>
                    seller_id !== null && toNumericViewId(v.seller_id) === seller_id
            )

        if (tableId !== TableViewId.Leads || seller_id === null) {
            return sellerViews
        }

        const presets = getLeadSystemPresetViews(seller_id).filter(
            (preset) =>
                !sellerViews.some(
                    (view) =>
                        normalizeSavedViewName(view.name) ===
                        normalizeSavedViewName(preset.name)
                )
        )

        return [...presets, ...sellerViews]
    }, [allViews, seller_id, tableId])

    useEffect(() => {
        hydrateActiveTableViewFromSession(dispatch, tableId, {
            views,
            sellerId: seller_id,
            hasLoaded,
        })
    }, [tableId, dispatch, views, seller_id, hasLoaded])

    useEffect(() => {
        if (!hasLoaded) {
            dispatch(TableViewsAction.fetchViews())
        }
    }, [hasLoaded, dispatch])

    const activeView =
        activeViewId !== null
            ? views.find((view) => toNumericViewId(view.id) === activeViewId) ?? null
            : null

    const matchingExistingView = useMemo(
        () =>
            findSavedViewByName(
                views.filter((view) => !isSystemSavedTableView(view)),
                viewName
            ),
        [views, viewName]
    )
    const isUpdatingExisting = Boolean(matchingExistingView)

    useEffect(() => {
        if (!open) {
            return
        }

        // Prefill with the active view so re-saving updates it instead of duplicating.
        setViewName(activeView?.name ?? '')
        inputRef.current?.focus()
        // Only when the panel opens — do not reset while typing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const buildCurrentState = useCallback(
        () => ({
            columnFilters: tableInit.columnFilters,
            globalFilter: tableInit.globalFilter,
            sorting: tableInit.sorting,
            columnVisibility: tableInit.columnVisibility,
            columnOrder: tableInit.columnOrder,
            pageSize: tableInit.pageSize,
        }),
        [tableInit]
    )

    const persistAndActivate = useCallback(
        (view: SavedTableView) => {
            const viewId = toNumericViewId(view.id)
            if (viewId === null) {
                return
            }
            dispatch(setActiveViewIdInRedux({ tableId, viewId }))
            persistActiveTableViewId(tableId, viewId)
        },
        [dispatch, tableId]
    )

    const handleSave = useCallback(async () => {
        const name = viewName.trim()
        if (!name || saving) return
        setSaving(true)
        try {
            const existing = findSavedViewByName(views, name)
            const existingId = existing ? toNumericViewId(existing.id) : null
            const saved = await dispatch(
                TableViewsAction.saveView({
                    ...(existingId != null ? { id: existingId } : {}),
                    name,
                    page_name: TABLE_VIEW_PAGE_NAME[tableId],
                    table_id: tableId,
                    state: buildCurrentState(),
                })
            )
            persistAndActivate(saved)
            setViewName('')
            toast.success(t(existingId != null ? 'View updated' : 'View saved'))
        } finally {
            setSaving(false)
        }
    }, [viewName, saving, tableId, views, buildCurrentState, persistAndActivate, dispatch, t])

    const handleUpdateView = useCallback(
        async (view: SavedTableView, e: React.MouseEvent) => {
            e.stopPropagation()
            if (isSystemSavedTableView(view)) {
                toast.warning(t('System views cannot be updated') as string)
                return
            }
            const viewId = toNumericViewId(view.id)
            if (viewId === null || saving) return
            setSaving(true)
            try {
                const saved = await dispatch(
                    TableViewsAction.saveView({
                        id: viewId,
                        name: view.name,
                        page_name: TABLE_VIEW_PAGE_NAME[tableId],
                        table_id: tableId,
                        state: buildCurrentState(),
                    })
                )
                persistAndActivate(saved)
                toast.success(t('View updated'))
            } finally {
                setSaving(false)
            }
        },
        [saving, tableId, buildCurrentState, persistAndActivate, dispatch, t]
    )

    const handleLoad = useCallback(
        (view: SavedTableView) => {
            const viewId = toNumericViewId(view.id)
            if (viewId === null) {
                return
            }

            tableInit.setColumnFilters(view.state.columnFilters)
            tableInit.setGlobalFilter(view.state.globalFilter)
            tableInit.setSorting(view.state.sorting)
            if (tableInit.setColumnVisibility) {
                tableInit.setColumnVisibility(view.state.columnVisibility)
            }
            if (tableInit.setColumnOrder && Array.isArray(view.state.columnOrder)) {
                tableInit.setColumnOrder(view.state.columnOrder)
            }
            tableInit.setPagination({ pageIndex: 0, pageSize: view.state.pageSize })
            dispatch(setActiveViewIdInRedux({ tableId, viewId }))
            persistActiveTableViewId(tableId, viewId)
            onClose()
        },
        [tableInit, tableId, dispatch, onClose]
    )

    const handleDelete = useCallback(
        (view: SavedTableView, e: React.MouseEvent) => {
            e.stopPropagation()
            if (isSystemSavedTableView(view)) {
                toast.warning(t('System views cannot be deleted') as string)
                return
            }
            const viewId = toNumericViewId(view.id)
            if (viewId === null) {
                return
            }

            dispatch(TableViewsAction.deleteView(viewId))
            if (activeViewId === viewId) {
                clearActiveTableView(dispatch, tableId)
            }
        },
        [dispatch, activeViewId, tableId, t]
    )

    useEffect(() => {
        if (!hasLoaded || activeViewId === null) {
            return
        }

        const exists = views.some((view) => toNumericViewId(view.id) === activeViewId)
        if (exists) {
            return
        }

        clearActiveTableView(dispatch, tableId)
    }, [activeViewId, tableId, views, dispatch, hasLoaded])

    if (!open) {
        return null
    }

    return (
        <div className="absolute right-0 top-full mt-1 z-50 w-80 max-w-[calc(100vw-1.5rem)] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-3 overflow-hidden">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                {t('Saved views')}
            </div>
            <div className="flex flex-col gap-2 mb-3">
                <input
                    ref={inputRef}
                    type="text"
                    value={viewName}
                    onChange={(e) => setViewName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                    placeholder={t('View name') as string}
                    className="w-full min-w-0 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
                <UiButton
                    size={UiButtonSize.Small}
                    style={UiButtonStyle.Primary}
                    onClick={handleSave}
                    title={isUpdatingExisting ? 'Update' : 'Save'}
                    disabled={!viewName.trim() || saving}
                    loading={saving}
                    fullWidth
                />
            </div>
            {views.length > 0 && (
                <ul className="space-y-0.5 max-h-40 overflow-y-auto mb-3">
                    {views.map((view) => {
                        const viewId = toNumericViewId(view.id)
                        const isActive = viewId !== null && activeViewId === viewId

                        return (
                        <li
                            key={view.id}
                            className={clsx(
                                'flex items-center justify-between gap-2 rounded px-1 py-1 group',
                                isActive
                                    ? 'bg-blue-50 dark:bg-blue-900/30'
                                    : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                            )}
                        >
                            <button
                                className={clsx(
                                    'text-sm text-left truncate flex-1 flex items-center gap-1',
                                    isActive
                                        ? 'text-blue-600 dark:text-blue-400 font-medium'
                                        : 'text-gray-800 dark:text-gray-100 hover:text-cta-light dark:hover:text-cta-light'
                                )}
                                onClick={() => handleLoad(view)}
                            >
                                {isActive && <CheckIcon className="w-3.5 h-3.5 shrink-0" />}
                                {view.name}
                            </button>
                            <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                {!isSystemSavedTableView(view) && (
                                    <>
                                        <button
                                            className="text-gray-300 hover:text-blue-500 dark:text-gray-600 dark:hover:text-blue-400"
                                            onClick={(e) => void handleUpdateView(view, e)}
                                            title={t('Update view with current filters') as string}
                                            disabled={saving}
                                        >
                                            <ArrowPathIcon className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            className="text-gray-300 hover:text-red-500 dark:text-gray-600 dark:hover:text-red-400"
                                            onClick={(e) => handleDelete(view, e)}
                                            title={t('Delete') as string}
                                        >
                                            <TrashIcon className="w-3.5 h-3.5" />
                                        </button>
                                    </>
                                )}
                            </div>
                        </li>
                        )
                    })}
                </ul>
            )}
            {views.length === 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">{t('No saved views')}</p>
            )}
            {tableInit.table && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-3 -mx-3">
                    <div className="px-3 pb-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        {t('Columns')}
                    </div>
                    <UiTableColumnVisibilityPanel
                        table={tableInit.table}
                        enabled={open}
                        showLoadedViewLabel={false}
                        onApplied={() => {
                            // Column changes mean the table no longer matches the saved view.
                            clearActiveTableView(dispatch, tableId)
                        }}
                    />
                </div>
            )}
        </div>
    )
}

export default UiTableViewDropdownPanel
